import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { useVbsOfferShowQuery } from "@/services/backend/vbs/offers/api/offer-api";
import { NavLink, useParams } from "react-router";
import { Offer } from "@/services/backend/vbs/offers/types/offer";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { ErrDocumentNotReady } from "@bitory-gmbh/bsm";
import {
  ErrDocumentNotReadyContextProvider,
  useErrDocumentNotReady,
} from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { DeleteOfferDialog } from "@/routes/processes/[processId]/vbs/offers/[offerId]/_components/delete-offer-dialog";
import { OfferPdf } from "@/routes/processes/[processId]/vbs/offers/[offerId]/_components/offer-pdf";
import { DuplicateOfferButton } from "@/routes/processes/[processId]/vbs/offers/[offerId]/_components/duplicate-offer-button";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentImmutableAlert } from "@/shared/bsm/core/document/components/ui/document-immutable-alert";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { VbsBreadcrumbs } from "@/routes/processes/[processId]/vbs/_components/vbs-breadcrumbs";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";

export function OfferRoute() {
  const { offerId } = useParams();

  const {
    data: offer,
    isLoading,
    error,
  } = useVbsOfferShowQuery({
    id: offerId!,
  });

  const breadcrumbs = offer && (
    <VbsBreadcrumbs customerId={offer.customerId} processId={offer.processId!}>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/vbs/${offer.processId!}/offers`}>
            {t("Angebote")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <DocumentNumberById documentId={offer.id} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </VbsBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {offer && <OfferPage offer={offer} />}
    </ProcessPageMainTemplate>
  );
}

function OfferPage(props: { offer: Offer }) {
  const { offer } = props;

  return (
    <PersistenceContextProvider>
      <ErrDocumentNotReadyContextProvider>
        <Page offer={offer} />
      </ErrDocumentNotReadyContextProvider>
    </PersistenceContextProvider>
  );
}

function Page({ offer }: { offer: Offer }) {
  const { notReadyError } = useErrDocumentNotReady();

  const links = [
    {
      to: "data",
      label: t("Angebot"),
      hasValidationError: hasOfferError(notReadyError),
    },
    { to: "calculation", label: t("Kalkulation") },
    { to: "treatment", label: t("Leistungen") },
    {
      to: "addresses-and-contacts",
      label: t("Adressen & Kontakte"),
      hasValidationError:
        hasAddressError(notReadyError) || hasRepresentativeError(notReadyError),
    },
    { to: "contract", label: t("Vertrag") },
  ];

  return (
    <DocumentPage>
      <DocumentImmutableAlert document={offer} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              hasValidationError={link.hasValidationError}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          {offer.immutable ? (
            <DuplicateOfferButton
              processId={offer.processId}
              offerId={offer.id}
            />
          ) : (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <DeleteOfferDialog offerId={offer.id} />
            </>
          )}
          <OfferPdf offer={offer} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}

function hasOfferError(error: ErrDocumentNotReady | null): boolean {
  if (!error) {
    return false;
  }

  const fields = [
    "date",
    "treatmentPrice.finalPrice",
    "pricesForExtras.ratePerAccessPanel",
    "pricesForExtras.hourlyRate",
  ];

  return fields.some((field) => field in error.errors);
}

function hasAddressError(error: ErrDocumentNotReady | null): boolean {
  if (!error) {
    return false;
  }

  const fields = [
    "serviceAddress.company",
    "serviceAddress.street",
    "serviceAddress.postalCode",
    "serviceAddress.city",
    "customerAddress.company",
    "customerAddress.street",
    "customerAddress.postalCode",
    "customerAddress.city",
  ];

  return fields.some((field) => field in error.errors);
}

function hasRepresentativeError(error: ErrDocumentNotReady | null): boolean {
  if (!error) {
    return false;
  }

  const fields = [
    "representative.email",
    "representative.mobilephone",
    "representative.name",
  ];

  return fields.some((field) => field in error.errors);
}
